import styles from './ManagementProfileTab.module.scss';
import useManagementProfileTabLogic from './useManagementProfileTabLogic';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import userImg from '../../../../assets/user.png';
import clsx from 'clsx';
import { toTitleCase } from '../../../../utils/utils';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import ManagementUserProfile from '../Overview/components/ManagementProfile/components/ManagementUserProfile';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { alertIcon, noDataFoundEmptyState } from '../../../../assets/hostedassets';
import greenTick from '../../../../assets/green-tick.svg';
import greyLock from '../../../../assets/grey-lock-2.svg';
import { LOCKED_COLUMN_TOOLTIPS, MANAGEMENT_PROFILE_HEADERS } from './ManagementProfileTabConstants';
import { DATE_FORMATS, formatDateByFormat, formatTenureYears } from '../../../../utils/dateUtils';
import { TabModule } from '../../../../utils/constants/commonConstants';

interface ManagementProfileTabProps {
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
}

function ManagementProfileTab({ navigateToTab }: ManagementProfileTabProps) {
    const { overviewLoading, directors, columnLocks, selectedDirector, setSelectedDirector } = useManagementProfileTabLogic();

    return (
        <div className={styles.ManagementProfileTab}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Management Profile</h2>
                </div>
                <div className={styles.headerRight}>
                    <div className={styles.dataSourceBadge}>
                        <span className={styles.dataSourceBadgeLabel}>Source:</span>
                        <span className={styles.dataSourceBadgeValue}>MCA</span>
                    </div>
                </div>
            </div>

            {overviewLoading ? (
                <LoadingSpinner color={'var(--primary-purple)'} height={'2.5rem'} style={{ margin: '3.75rem 0' }} />
            ) : directors.length === 0 ? (
                <div className={styles.emptyState}>
                    <img src={noDataFoundEmptyState} alt="" className={styles.emptyStateIcon} />
                    <div className={styles.emptyStateTitle}>No data available</div>
                </div>
            ) : (
                <div className={styles.content}>
                    <div className={styles.table}>
                        <div className={styles.tableHeader}>
                            {MANAGEMENT_PROFILE_HEADERS.map(({ label, lockKey }) => {
                                const locked = lockKey ? columnLocks[lockKey] : false;
                                if (!locked || !lockKey) return <div key={label}>{label}</div>;
                                return (
                                    <div key={label}>
                                        <Tooltip
                                            content={LOCKED_COLUMN_TOOLTIPS[lockKey]}
                                            direction="top"
                                            usePortal
                                            style={{ width: '240px', color: 'var(--icon-color)' }}
                                        >
                                            <span className={styles.lockedHeaderInner}>
                                                {label}
                                                <img src={greyLock} alt="" className={styles.lockedHeaderIcon} />
                                            </span>
                                        </Tooltip>
                                    </div>
                                );
                            })}
                        </div>

                        {directors.map((row, idx) => (
                            <div
                                key={`${row.din}-${idx}`}
                                className={clsx(styles.tableRow, styles.clickableRow)}
                                onClick={() => setSelectedDirector(row)}
                            >
                                <div className={styles.nameCell}>
                                    <div className={styles.avatar}>
                                        <img
                                            src={row.imageUrl || userImg}
                                            alt={row.name}
                                            onError={({ currentTarget }) => {
                                                currentTarget.onerror = null;
                                                currentTarget.src = userImg;
                                            }}
                                        />
                                    </div>
                                    <div className={styles.nameInfo}>
                                        <span className={styles.directorName}>{toTitleCase(row.name)}</span>
                                        {row.linkedinUrl && row.followersCount && (
                                            <a
                                                href={row.linkedinUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.linkedInLink}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span className={styles.followersCount}>{row.followersCount}</span>
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.cellText}>
                                    {row.designation ? (
                                        <span className={styles.cellTextValue} title={row.designation}>
                                            {row.designation}
                                        </span>
                                    ) : (
                                        <span className={styles.cellDash}>-</span>
                                    )}
                                </div>

                                <div>
                                    {row.tenureBeginDate ? (
                                        <div className={styles.tenureInfo}>
                                            <span className={styles.tenureYears}>
                                                {formatTenureYears(row.tenureBeginDate, row.tenureEndDate)}
                                            </span>
                                            <span className={styles.tenureDates}>
                                                ({formatDateByFormat(row.tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR)} -{' '}
                                                {row.tenureEndDate
                                                    ? formatDateByFormat(row.tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR)
                                                    : row.isActive
                                                    ? 'Present'
                                                    : ''}
                                                )
                                            </span>
                                        </div>
                                    ) : (
                                        <span className={styles.cellDash}>-</span>
                                    )}
                                </div>

                                <div>
                                    {row.loading ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.email || row.phone ? (
                                        <div className={styles.contactInfo}>
                                            {row.email && <span className={styles.contactEmail}>{row.email}</span>}
                                            {row.phone && <span className={styles.contactPhone}>{row.phone}</span>}
                                        </div>
                                    ) : (
                                        <span className={styles.cellDash}>-</span>
                                    )}
                                </div>

                                <div
                                    className={clsx({
                                        [styles.alertClickable]: !columnLocks.riskAlerts && !row.loading && !!row.riskIndicatorCount,
                                    })}
                                    onClick={
                                        !columnLocks.riskAlerts && !row.loading && row.riskIndicatorCount
                                            ? (e) => {
                                                  e.stopPropagation();
                                                  navigateToTab?.(TabModule.RELATED_PARTY, {
                                                      directorName: row.name ?? '',
                                                      directorDin: row.din ?? '',
                                                  });
                                              }
                                            : undefined
                                    }
                                >
                                    {columnLocks.riskAlerts ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.loading ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.riskIndicatorCount == null ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.riskIndicatorCount > 0 ? (
                                        <span className={styles.alertRed}>
                                            <img src={alertIcon} alt="" height="14px" />
                                            {`${row.riskIndicatorCount} ${row.riskIndicatorCount === 1 ? 'Alert' : 'Alerts'}`}
                                        </span>
                                    ) : (
                                        <span className={styles.alertGrey}>
                                            <img src={greenTick} alt="" height="14px" />
                                            No Alerts
                                        </span>
                                    )}
                                </div>

                                <div
                                    className={clsx({
                                        [styles.alertClickable]: !columnLocks.regulatoryChecks && !row.loading && !!row.amlPepRiskCount,
                                    })}
                                    onClick={
                                        !columnLocks.regulatoryChecks && !row.loading && row.amlPepRiskCount
                                            ? (e) => {
                                                  e.stopPropagation();
                                                  navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                                      directorName: row.name ?? '',
                                                      directorDin: row.din ?? '',
                                                      section: 'regulatoryChecks',
                                                  });
                                              }
                                            : undefined
                                    }
                                >
                                    {columnLocks.regulatoryChecks ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.loading ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.amlPepRiskCount == null ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.amlPepRiskCount > 0 ? (
                                        <span className={styles.alertRed}>
                                            <img src={alertIcon} alt="" height="14px" />
                                            {`${row.amlPepRiskCount} ${row.amlPepRiskCount === 1 ? 'Indicator' : 'Indicators'}`}
                                        </span>
                                    ) : (
                                        <span className={styles.alertGrey}>
                                            <img src={greenTick} alt="" height="14px" />
                                            No Indicator
                                        </span>
                                    )}
                                </div>

                                <div
                                    className={clsx({
                                        [styles.alertClickable]: !columnLocks.legalCases && !row.loading && !!row.legalCasesCount,
                                    })}
                                    onClick={
                                        !columnLocks.legalCases && !row.loading && row.legalCasesCount
                                            ? (e) => {
                                                  e.stopPropagation();
                                                  navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                                      directorName: row.name ?? '',
                                                      directorDin: row.din ?? '',
                                                  });
                                              }
                                            : undefined
                                    }
                                >
                                    {columnLocks.legalCases ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.loading ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : row.legalCasesCount == null || row.legalCasesCount === 0 ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : (
                                        <span className={styles.alertRed}>
                                            <img src={alertIcon} alt="" height="14px" />
                                            {`${row.legalCasesCount} ${row.legalCasesCount === 1 ? 'Case' : 'Cases'}`}
                                        </span>
                                    )}
                                </div>

                                <div>
                                    {row.isActive === null || row.isActive === undefined ? (
                                        <span className={styles.cellDash}>-</span>
                                    ) : (
                                        <span
                                            className={clsx(styles.statusBadge, row.isActive ? styles.statusActive : styles.statusFormer)}
                                        >
                                            {row.isActive ? 'Active' : 'Former'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selectedDirector && (
                <SideDrawer onClose={() => setSelectedDirector(null)} width={'60%'}>
                    <ManagementUserProfile
                        showUserProfile={selectedDirector}
                        dataPreloaded
                        redirectToCreditBureau={(directorId: string, bureauType: string) => {
                            setSelectedDirector(null);
                            navigateToTab?.('creditBureau', { directorId, report: 'keyAccountsDetailedReport', bureauType });
                        }}
                        redirectToCompliances={() => {
                            setSelectedDirector(null);
                            navigateToTab?.('legalChecks');
                        }}
                    />
                </SideDrawer>
            )}
        </div>
    );
}

export default ManagementProfileTab;
